import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AdminMonthlyLeaderboard from "./AdminMonthlyLeaderboard";
import { WithdrawalRequest } from "./WithdrawalRequest";
import { AdminInfluencersDashboard } from "./influencers/AdminInfluencersDashboard";
import PublicLeaderboard from "@/pages/PublicLeaderboard";
import { DashboardHeader } from "./DashboardHeader";
import { TabNavigation } from "./TabNavigation";
import { DashboardHome } from "./DashboardHome";
import { useCouponData } from "./hooks/useCouponData";

type TabId =
  | "dashboard"
  | "public-ranking"
  | "reports"
  | "withdrawal-requests"
  | "admin-influencers";

export const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>("dashboard");
  const { user, signOut, userRole } = useAuth();
  const {
    userCoupon,
    setUserCoupon,
    couponAnalysis,
    lastUpdated,
    dateRange,
    setMonthRange,
    loading,
    couponLoading,
    availableBalance,
  } = useCouponData();

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success("Logout realizado com sucesso!");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      window.location.replace("/");
    }
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="dashboard-dark min-h-screen bg-background">
      <DashboardHeader
        user={user}
        onLogout={handleLogout}
        onNavigateToRanking={() => setActiveTab("public-ranking")}
        onNavigateToWallet={() => setActiveTab("withdrawal-requests")}
        onNavigateToDashboard={() => setActiveTab("dashboard")}
        activeTab={activeTab}
      />
      {userRole === "admin" && (
        <TabNavigation activeTab={activeTab} userRole={userRole} onTabChange={setActiveTab} />
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {activeTab === "dashboard" ? (
          <DashboardHome
            userCoupon={userCoupon}
            couponAnalysis={couponAnalysis}
            couponLoading={couponLoading}
            lastUpdated={lastUpdated}
            dateRange={dateRange}
            onMonthSelect={setMonthRange}
            onNavigateToWallet={() => setActiveTab("withdrawal-requests")}
            availableBalance={availableBalance}
            isAdmin={userRole === "admin"}
            onCouponChange={setUserCoupon}
          />
        ) : activeTab === "public-ranking" ? (
          <PublicLeaderboard />
        ) : activeTab === "reports" && userRole === "admin" ? (
          <AdminMonthlyLeaderboard />
        ) : activeTab === "admin-influencers" && userRole === "admin" ? (
          <AdminInfluencersDashboard />
        ) : activeTab === "withdrawal-requests" ? (
          <WithdrawalRequest />
        ) : null}
      </div>
    </div>
  );
};

function DashboardSkeleton() {
  return (
    <div className="dashboard-dark min-h-screen bg-background">
      {/* Header Skeleton */}
      <div className="border-b border-white/10 bg-black/40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white/10 rounded-full animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-6 w-32 bg-white/10 rounded animate-pulse"></div>
                <div className="h-4 w-48 bg-white/10 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="h-8 w-24 bg-white/10 rounded animate-pulse"></div>
              <div className="h-8 w-16 bg-white/10 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Skeleton */}
      <div className="border-b border-white/10 bg-black/30 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <div className="h-12 w-20 bg-white/10 rounded animate-pulse"></div>
            <div className="h-12 w-24 bg-white/10 rounded animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Content Skeleton */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <div className="space-y-2">
              <p className="text-lg font-medium">Carregando seu dashboard...</p>
              <p className="text-sm text-muted-foreground">Buscando dados do seu cupom e vendas</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
